import { PoolStats, StakeholderStats } from '../types/snapshot.types';
import { calcTotal } from './reduce-utils.util';
import { toNumber } from './to-number.util';

export type PoolAllocation = {
  granted: number;
  ungranted: number;
};

export type EquityPoolSplit = {
  equity: number;
  pool: number;
};

export const isPoolGrantable = (pool: PoolStats): boolean =>
  !pool.isTerminated || new Date(pool.terminationDate).getTime() > Date.now();

// Mirrors the cap table "Equity Pools Grantable" / "Equity Pools Granted" rows:
// ungranted is the remainder of still-active pools, granted is what is still held
// inside the pool as unvested grants. Vested/exercised shares already left the pool
// into stakeholder rows, so `poolDilutedShares - poolGrantedShares` overstates both.
export const getPoolAllocation = (pools: PoolStats[] = []): PoolAllocation => ({
  granted: Math.max(
    0,
    calcTotal(pools, ({ unvested }) => toNumber(unvested)),
  ),
  ungranted: Math.max(
    0,
    calcTotal(pools.filter(isPoolGrantable), ({ dilluted }) => toNumber(dilluted)),
  ),
});

// Stakeholder rows already carry the shares granted out of a pool, so the pool's own
// share of the diluted total is whatever those rows leave over — the same split the cap
// table draws between its stakeholder rows and the equity-pool rows. Summing
// `poolDilutedShares + issuedShares` instead double counts the grantable pool whenever
// the company issued-shares formula is ISSUED_SHARES_AND_AVAILABLE_POOL.
export const getEquityPoolSplit = ({
  dilutedShares,
  stakeholders = [],
}: {
  dilutedShares: number;
  stakeholders?: StakeholderStats[];
}): EquityPoolSplit => {
  const equity = Math.max(
    0,
    calcTotal(stakeholders, ({ diluted }) => toNumber(diluted)),
  );

  return { equity, pool: Math.max(0, toNumber(dilutedShares) - equity) };
};
